// Hook para consulta de CEP com debounce, usado no RegisterForm para
// preencher endereços automaticamente.

import { useCallback, useRef } from 'react';
import { addressService } from '../services';
import { unformat } from '../utils/formatters';
import logger from '../utils/logger';

// Mapeia um registro de CEP da API de endereço para os nomes dos campos do formulário
// de cadastro. Ex: tipo = 'residencial' → logradouroResidencial, cepResidencialId...
// registro.id é o ID do registro de CEP, que a API de cadastro espera (não o CEP em
// texto) em cep{Tipo}Id.
export const mapearEnderecoCadastro = (registro, tipo) => {
  const prefix = tipo.charAt(0).toUpperCase() + tipo.slice(1);

  return {
    [`logradouro${prefix}`]: registro.logradouro || '',
    [`bairro${prefix}`]: registro.bairro || '',
    [`pais${prefix}`]: registro.paisId ? String(registro.paisId) : '1',
    [`estado${prefix}`]: registro.estadoId ? String(registro.estadoId) : '',
    [`municipio${prefix}`]: registro.cidadeId ? String(registro.cidadeId) : '',
    [`cep${prefix}Id`]: registro.id ?? null
  };
};

export const useCEP = () => {
  // Referência ao setTimeout do debounce, para poder cancelar se o usuário continuar digitando
  const timeoutRef = useRef(null);

  /**
   * callbacks:
   * - onSuccess(addressData, estadoId): CEP com um único registro, já mapeado pro form;
   * - onMultiplos(registros): CEP com mais de um registro (CEP único de localidade) —
   *   quem chama mostra as opções e mapeia a escolhida com mapearEnderecoCadastro;
   * - onError(errors): CEP não encontrado ou erro técnico.
   */
  const buscarCEP = useCallback(async (cep, tipo, callbacks) => {
    const { onSuccess, onMultiplos, onError } = callbacks || {};

    try {
      const cepNumeros = unformat(cep);

      if (cepNumeros.length !== 8) {
        onError?.({ [`cep${tipo}`]: 'CEP deve ter 8 dígitos' });
        return;
      }

      // `data.data` vem sempre como array (ver addressService.getByCEP). CEP não
      // encontrado vem como erro HTTP 404, tratado no catch abaixo.
      const response = await addressService.getByCEP(cepNumeros);
      const registros = response.data?.data || [];

      if (registros.length === 1) {
        const [registro] = registros;
        // Repassa o estadoId para quem chamou poder recarregar a lista de municípios
        onSuccess?.(mapearEnderecoCadastro(registro, tipo), registro.estadoId);
      } else if (registros.length > 1) {
        onMultiplos?.(registros);
      } else {
        onError?.({ [`cep${tipo}`]: 'CEP não encontrado' });
      }
    } catch (error) {
      // CEP não encontrado vem como 404 da API - trata como caso esperado, não erro técnico
      const naoEncontrado = error.response?.status === 404;
      const errorMsg = naoEncontrado ? 'CEP não encontrado' : 'Erro ao buscar CEP';

      if (!naoEncontrado) logger.error('Erro ao buscar CEP:', error);

      onError?.({ [`cep${tipo}`]: errorMsg });
    }
  }, []);

  const buscarCEPDebounced = useCallback((cep, tipo, callbacks, delay = 500) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      buscarCEP(cep, tipo, callbacks);
    }, delay);
  }, [buscarCEP]);

  return { buscarCEPDebounced };
};
